import React, { useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

// import config
import { GRAPHQL_API_URL, THANK_YOU_PAGE_URL } from '../../util/config';

// import hooks
import { useGraphqlFetch, useFormReducer } from '../../hooks';

// import functions
import {
  checkForFieldsWithErrors,
  checkForUntouchedFields,
  sendFormData,
} from './functions';

// import styled components
import { Spinner } from '../styled';
import {
  FormError,
  FormField,
  FormInput,
  FormLabel,
  FormPhoneInput,
  FormSelect,
  FormSelectWrapper,
  FormSpinnerWrapper,
  FormSubmit,
  FormSubmitWrapper,
  FormWrapper,
  ScrollPixel,
} from './styled';

// import svg
import FormToggleArrow from '../../svgs/form-toggle-arrow.svg';

const Form = ({ ads__referral_url__c, ads__landing_url__c }) => {
  /**
   * note that the state options are included in this query and json file b/c Gatsby only allows one static query per component
   * thus the dumb lookin way the values are formatted with ABBR.Name in form.json and the mapping of the options below in drawSelectField(), they have to be stored as strings
   * as of 9/5/2019 now uses list of states from GraphQL API as preferred data source, but keep the local json available as a fallback if there's an issue with the remote response
   */
  const {
    allFormJson: { nodes },
  } = useStaticQuery(
    graphql`
      query {
        allFormJson {
          nodes {
            error
            label
            name
            type
            validator
            values
          }
        }
      }
    `
  );

  const statesListQuery = `
  {
    statesList {
      abbreviation
      name
    }
  }
  `;

  const [formExpanded, setFormExpanded] = useState(true);
  const [formScrolled, setFormScrolled] = useState(false);
  const [sending, setSending] = useState(false);
  const [successfulSubmission, setSuccessfulSubmission] = useState(false);
  const [windowWidth, setWindowWidth] = useState(0);

  const [statesListData, statesListError, statesListLoading] = useGraphqlFetch(
    GRAPHQL_API_URL,
    statesListQuery,
    false
  );

  useEffect(() => {
    // prefer window.visualViewport since it works with Chrome device mode, fall back to innerWidth if not supported
    const windowWidth = window.visualViewport
      ? window.visualViewport.width
      : window.innerWidth;

    setWindowWidth(windowWidth);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      // prefer boundinClientRect.y, fall back to boundingClientRect.top if not supported/polyfilled
      const scrollBoundary = entries[0].boundingClientRect.y
        ? entries[0].boundingClientRect.y
        : entries[0].boundingClientRect.top;

      if (scrollBoundary > 0) {
        setFormScrolled(false);
      } else {
        setFormScrolled(true);
        setFormExpanded(false);
      }
    });
    observer.observe(document.getElementById('scroll-pixel'));

    return () => observer.disconnect();
  }, []);

  const [formState, dispatch] = useFormReducer();

  const drawTextField = field => {
    const { error, label, name, validator } = field;

    return (
      <FormField key={name}>
        <FormInput
          id={name}
          name={name}
          value={formState[name].value}
          onChange={e =>
            dispatch({
              type: 'handleChange',
              name,
              value: e.target.value,
              validator,
            })
          }
          required
        />
        <FormLabel htmlFor={name}>{label}</FormLabel>
        {formState[name].error ? (
          <FormError className="error">{error}</FormError>
        ) : (
          <FormError>&nbsp;</FormError>
        )}
      </FormField>
    );
  };

  const drawPhoneField = field => {
    const { error, label, name, validator } = field;

    return (
      <FormField key={name}>
        <FormPhoneInput
          id={name}
          name={name}
          value={formState[name].value}
          format="(###) ###-####"
          onChange={e =>
            dispatch({
              type: 'handleChange',
              name,
              value: e.target.value,
              validator,
            })
          }
          required
        />
        <FormLabel htmlFor={name}>{label}</FormLabel>
        {formState[name].error ? (
          <FormError className="error">{error}</FormError>
        ) : (
          <FormError>&nbsp;</FormError>
        )}
      </FormField>
    );
  };

  const drawSelectField = field => {
    const { error, name, validator } = field;

    const isUsingRemoteData =
      statesListError || statesListLoading ? false : true;

    const statesList =
      statesListError || statesListLoading
        ? nodes[5].values
        : statesListData.statesList;

    return (
      <FormField key={name}>
        <FormSelectWrapper>
          <FormSelect
            id={name}
            name={name}
            value={formState[name].value}
            onChange={e =>
              dispatch({
                type: 'handleChange',
                name,
                value: e.target.value,
                validator,
              })
            }
            required
            aria-label={name}
          >
            {statesList.map((opt, i) => {
              const splitIndex = isUsingRemoteData ? null : opt.indexOf('.');
              const abbreviation = isUsingRemoteData
                ? opt.abbreviation
                : opt.substr(0, splitIndex);
              const name = isUsingRemoteData
                ? opt.name
                : opt.substr(splitIndex + 1, opt.length);

              if (i === 0)
                return (
                  <option key={name} value="" disabled defaultValue>
                    {name}
                  </option>
                );

              return (
                <option key={abbreviation} value={abbreviation}>
                  {name}
                </option>
              );
            })}
          </FormSelect>
          <span className="tooltip" tabIndex="0">
            ?
          </span>
          <span className="tooltip-content">
            We are able to provide home financing solutions to clients in the
            states in which we are licensed as OneTrust Home Loans. Please see
            our{' '}
            <a
              href="https://onetrusthomeloans.com/licensing-information/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Locations & Licensing page
            </a>{' '}
            for more details.
          </span>
        </FormSelectWrapper>
        {formState[name].error ? (
          <FormError className="error">{error}</FormError>
        ) : (
          <FormError>&nbsp;</FormError>
        )}
      </FormField>
    );
  };

  const drawFields = fields => {
    return fields.map(field => {
      if (field.type === 'statesList') return null;

      if (field.name === 'phone') return drawPhoneField(field);

      if (field.type === 'text') return drawTextField(field);

      if (field.type === 'select') return drawSelectField(field);

      return null;
    });
  };

  const submitForm = async e => {
    e.preventDefault();

    const untouchedFields = checkForUntouchedFields(formState, dispatch);

    if (untouchedFields.length > 0) return;

    const fieldsWithErrors = checkForFieldsWithErrors(formState);

    if (fieldsWithErrors.length > 0) return;

    setSending(true);

    const apiResponse = await sendFormData(
      formState,
      ads__referral_url__c,
      ads__landing_url__c
    );

    setSending(false);

    if (apiResponse.status == 0) {
      setSuccessfulSubmission(true);
    }
  };

  if (successfulSubmission) {
    window.location.href = THANK_YOU_PAGE_URL;

    return (
      <>
        <FormWrapper
          id="form"
          formExpanded={formExpanded}
          formScrolled={formScrolled}
          successful={true}
          windowWidth={windowWidth}
        >
          <h2 id="success-title">Thank You for your request!</h2>
          <h3>
            A OneTrust Condotel Loan Specialist will be contacting you shortly.
          </h3>
        </FormWrapper>
        <ScrollPixel id="scroll-pixel" />
      </>
    );
  }

  return (
    <>
      <FormWrapper
        id="form"
        formExpanded={formExpanded}
        formScrolled={formScrolled}
        windowWidth={windowWidth}
      >
        <h2>
          Request a Consultation with a <br /> Condotel Loan Specialist
          {formScrolled ? (
            <span
              onClick={() => setFormExpanded(!formExpanded)}
              onKeyPress={() => setFormExpanded(!formExpanded)}
              tabIndex="0"
            >
              <FormToggleArrow />
            </span>
          ) : null}
        </h2>
        {sending ? (
          <FormSpinnerWrapper>
            <Spinner />
          </FormSpinnerWrapper>
        ) : (
          <form onSubmit={e => submitForm(e)} noValidate>
            {drawFields(nodes)}
            <FormSubmitWrapper>
              <FormSubmit type="submit" value="Request a Consultation" />
            </FormSubmitWrapper>
            <p className="disclaimer">
              By clicking the ‘Request a Consultation’ button above, I authorize
              OneTrust Home Loans to contact me using an automated dialing
              system, even if the telephone number is a cellular phone or other
              service for which the party is charged. I understand that I am not
              required to provide this consent in order to obtain goods or
              services from OneTrust Home Loans. I agree to receive disclosures
              and communications in electronic form and I confirm that I have a
              working computer system and an internet website browser to view
              this webpage and this loan inquiry form. I agree that OneTrust
              Home Loans may contact me by mail, telephone (mobile devices
              including SMS and MMS) or email in connection with my request even
              if my telephone number or email address appears on a OneTrust Home
              Loans internal Do Not Call / Do Not Email List, a State or
              National Do Not Call Registry, a Do Not E-Mail list, or any other
              Do Not Contact List.
            </p>
          </form>
        )}
      </FormWrapper>
      <ScrollPixel id="scroll-pixel" />
    </>
  );
};

export default Form;
